/**
 * ContactReviewQueuePage — Phase 1 of Contact Hub.
 *
 * Lists flagged candidate pairs from /api/contacts/review-queue with a
 * side-by-side field diff. Reviewer picks Merge (with primary side) or
 * Keep Separate.
 *
 * Locked design:
 * - 'flag' rows show the reason (different_name | cross_boundary | phone_name
 *   | fuzzy_name) so reviewers understand why the pair was caught.
 * - 'auto_merge' candidates also appear here in Phase 1 (one-click approve);
 *   actual auto-execution waits for Phase 4 once we have confidence.
 * - Every merge is reversible — backend snapshots pre-merge state and
 *   supports POST /api/contacts/merge/undo.
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const REASON_LABELS = {
  different_name: { text: 'Same email, different names', tone: 'amber' },
  cross_boundary: { text: 'Personal ↔ tenant boundary', tone: 'red' },
  phone_name: { text: 'Phone + name match (no email)', tone: 'amber' },
  fuzzy_name: { text: 'Fuzzy name match', tone: 'sky' },
};

const TONE_CLASSES = {
  amber: 'bg-amber-100 text-amber-900 border-amber-300',
  red: 'bg-red-100 text-red-900 border-red-300',
  sky: 'bg-sky-100 text-sky-900 border-sky-300',
  emerald: 'bg-emerald-100 text-emerald-900 border-emerald-300',
};

export default function ContactReviewQueuePage() {
  const [pairs, setPairs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [resolving, setResolving] = useState(null);

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
  });

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/contacts/review-queue`, authHeader());
      setPairs(res.data?.pairs || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load review queue');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  const runScan = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/contacts/review-queue/scan`, {}, authHeader());
      const s = res.data;
      toast.success(
        `Scanned ${s.contacts_scanned} contacts — ${s.new_flagged} flagged, ${s.new_auto_merge_candidates} auto-merge candidates`,
      );
      await fetchQueue();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const resolvePair = async (pair, action, primaryId = null) => {
    setResolving(pair.id);
    try {
      await axios.post(
        `${API}/contacts/review-queue/${pair.id}/resolve`,
        { action, ...(primaryId ? { primary_id: primaryId } : {}) },
        authHeader(),
      );
      toast.success(action === 'merge' ? 'Merged (reversible)' : 'Kept separate');
      setPairs(prev => prev.filter(p => p.id !== pair.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resolve');
    } finally {
      setResolving(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="contact-review-queue-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Contact Review Queue</h1>
          <p className="text-sm text-stone-600 mt-1">
            Candidate duplicate pairs the dedupe spine flagged. Each merge is reversible.
          </p>
        </div>
        <Button
          onClick={runScan}
          disabled={scanning}
          data-testid="run-scan-btn"
        >
          {scanning ? 'Scanning…' : 'Run scan'}
        </Button>
      </div>

      {loading && <p className="text-sm text-stone-500">Loading…</p>}

      {!loading && pairs.length === 0 && (
        <Card data-testid="review-queue-empty">
          <CardContent className="p-8 text-center text-stone-500">
            No pending pairs. Run a scan to surface candidates.
          </CardContent>
        </Card>
      )}

      {pairs.map(pair => {
        const isAuto = pair.decision === 'auto_merge';
        const reasonInfo = REASON_LABELS[pair.reason] || { text: pair.rule, tone: 'sky' };
        const tone = isAuto ? 'emerald' : reasonInfo.tone;
        const a = pair.contact_a;
        const b = pair.contact_b;
        const diff = pair.field_diff || {};
        const diffKeys = Object.keys(diff);

        return (
          <Card key={pair.id} data-testid={`review-pair-${pair.id}`}>
            <CardHeader className="flex flex-row items-center justify-between gap-4 pb-3">
              <div className="flex items-center gap-3">
                <Badge
                  className={`border ${TONE_CLASSES[tone]} px-2.5 py-1`}
                  data-testid={`pair-reason-${pair.id}`}
                >
                  {isAuto ? 'Auto-merge candidate' : reasonInfo.text}
                </Badge>
                <span className="text-xs text-stone-500">
                  confidence {(pair.confidence * 100).toFixed(0)}% · rule {pair.rule}
                </span>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <ContactColumn label="Contact A" contact={a} diffKeys={diffKeys} />
                <ContactColumn label="Contact B" contact={b} diffKeys={diffKeys} />
              </div>

              <div className="flex flex-wrap gap-2 pt-2 border-t border-stone-200">
                <Button
                  size="sm"
                  disabled={resolving === pair.id}
                  onClick={() => resolvePair(pair, 'merge', a.id)}
                  data-testid={`merge-keep-a-btn-${pair.id}`}
                >
                  Merge → keep A
                </Button>
                <Button
                  size="sm"
                  disabled={resolving === pair.id}
                  onClick={() => resolvePair(pair, 'merge', b.id)}
                  data-testid={`merge-keep-b-btn-${pair.id}`}
                >
                  Merge → keep B
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={resolving === pair.id}
                  onClick={() => resolvePair(pair, 'keep_separate')}
                  data-testid={`keep-separate-btn-${pair.id}`}
                >
                  Keep separate
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

function ContactColumn({ label, contact, diffKeys }) {
  return (
    <div className="rounded-md border border-stone-200 p-3 bg-stone-50">
      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">
        {label}
      </p>
      <p className="font-medium">
        {contact.first_name} {contact.last_name}
      </p>
      <p className="text-stone-600">{contact.email || '—'}</p>
      <p className="text-stone-600 text-xs mt-1">
        {contact.phone || contact.phone_mobile || '—'}
        {contact.company ? ` · ${contact.company}` : ''}
      </p>
      <p className="text-stone-500 text-xs mt-1">
        category {contact.category || '—'} · tier {contact.ownership_tier || 'tenant'}
      </p>
      {diffKeys.length > 0 && (
        <p className="text-xs text-amber-700 mt-2 break-all">
          differs on: {diffKeys.join(', ')}
        </p>
      )}
    </div>
  );
}
